import { usePushNotifications } from './usePushNotifications.ts';

/**
 * Turning "tell me when the list changes" on, for this device.
 *
 * **On the Profile page, and below the home-screen section, for two reasons.** Permission
 * to notify belongs to the browser rather than to the account, so like installing it
 * describes the phone in your hand — somebody with this on at home should still see it off
 * on their laptop. And on iPhone it is only offered *after* the app is on the home screen,
 * so the section that explains how to do that has to be the one directly above.
 *
 * It draws nothing at all when the server has no VAPID keys configured, which is the
 * default. An offer that cannot be fulfilled is worse than no offer.
 */
export function NotificationSettings() {
  const { available, blocker, subscribed, isLoading, isBusy, error, enable, disable } =
    usePushNotifications();

  // Nothing while the answer is unknown: a section that appears a moment after the page
  // does, and sometimes not at all, reads as a glitch.
  if (isLoading || !available) {
    return null;
  }

  // No Push API and no route to one — an in-app browser, or something very old. There is
  // no advice to give, so there is nothing to say.
  if (blocker === 'unsupported') {
    return null;
  }

  return (
    <section className="settings-section">
      <h3 className="settings-section__title">Notifications</h3>

      {blocker === 'needs-install' && (
        <p className="settings-hint">
          Add Listocial to your home screen first — on iPhone, notifications only work once
          it’s an icon rather than a Safari tab.
        </p>
      )}

      {blocker === 'denied' && (
        <p className="settings-hint">
          Notifications are blocked for Listocial. Your browser’s settings for this site are
          the only place that can turn them back on — nothing here can ask again.
        </p>
      )}

      {blocker === null && (
        <>
          <label className="toggle">
            <input
              type="checkbox"
              checked={subscribed}
              onChange={(event) => (event.target.checked ? enable() : disable())}
              disabled={isBusy}
            />
            <span>
              Tell me when the list changes
              <span className="toggle__hint">
                {subscribed
                  ? 'One quiet summary a minute or so after somebody adds or ticks something — not one per tap. Nothing while you have the list open.'
                  : 'Nothing is sent. You’ll see what changed the next time you open the app.'}
              </span>
            </span>
          </label>

          <p className="settings-hint">
            This is for this device only. Your other phones and computers are unaffected.
          </p>
        </>
      )}

      {error && (
        <p className="form-error" role="alert">
          {error.message}
        </p>
      )}
    </section>
  );
}
